import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, CheckCheck, Edit2, ListTodo, Loader2, X } from "lucide-react";

import { useGetChannelMessages } from "components/ChatWidget/hooks/use-get-channel-messages";
import { useGetDmMessages } from "components/ChatWidget/hooks/use-get-dm-messages";
import { useMarkDmRead, useMarkGroupRead } from "components/ChatWidget/hooks/use-mark-chat-read";
import { useScrollChatToNewestMessage } from "components/ChatWidget/hooks/use-scroll-chat-to-newest-message";
import { useSendChannelMessage } from "components/ChatWidget/hooks/use-send-channel-message";
import { useSendChatMessage } from "components/ChatWidget/hooks/use-send-chat-message";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";

import chatWidgetStyles from "../chat-widget.module.css";
import { useEditChatMessage } from "../hooks/use-edit-chat-message";
import { ScrollArea } from ".//ui/scroll-area";

// Match the sidebar avatar treatment exactly so the chat surface reads
// as one product instead of two. Initials on a hashed-from-id colour,
// with profilePic taking precedence when present.
const AVATAR_PALETTE = [
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
];

const avatarColorFor = (id) => {
  if (id == null) return AVATAR_PALETTE[0];
  const n = Number(id);
  if (!Number.isFinite(n)) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[Math.abs(n) % AVATAR_PALETTE.length];
};

const initialsFromName = (firstName, lastName) => {
  const a = (firstName || "").trim().charAt(0).toUpperCase();
  const b = (lastName || "").trim().charAt(0).toUpperCase();
  return `${a}${b}` || a || b || "?";
};

// Inline avatar matching the sidebar — small, circular, brand-coloured.
// Uses the user's profilePic when present; falls back to a coloured
// circle with their initials. If the image URL fails to load (404,
// broken CDN, etc.) we swap to the initials at render time so we
// never leave a broken-image icon in the message stream.
const MessageAvatar = ({ profilePic, idForColor, initials, size = 34 }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const hasImage = !!profilePic && !imageFailed;

  const fallbackCircle = (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: avatarColorFor(idForColor),
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size <= 28 ? "0.6875rem" : "0.8125rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );

  if (!hasImage) return fallbackCircle;

  return (
    <img
      src={profilePic}
      alt=""
      onError={() => setImageFailed(true)}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
        display: "block",
      }}
    />
  );
};

export const ChatMessages = ({ currentView, activeChatStaffId, channelId, onCreateTask }) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);

  const [activeMessageMenu, setActiveMessageMenu] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");
  // Inline 403 / "can't edit" notice anchored to the message being
  // edited. Cleared every time a new edit starts or the input closes.
  const [editError, setEditError] = useState(null);

  const { data: staffMember } = useStaffMembers(tenantVal);
  const { data: me } = useStaffInfo();
  const editMessage = useEditChatMessage();
  const markDmRead = useMarkDmRead();
  const markGroupRead = useMarkGroupRead();
  const queryClient = useQueryClient();
  // The two send hooks are mounted unconditionally so a resend works
  // whether we're viewing a DM or a channel. Each receives the active
  // id (or null when not relevant) and is only called from the retry
  // click handler below.
  const resendDirectMessage = useSendChatMessage(activeChatStaffId);
  const resendChannelMessage = useSendChannelMessage(channelId);

  // Both caches are now an infinite-query shape with the same
  // {pages:[{messages:[...]}]} structure, so the drop-by-id helper
  // applies uniformly to DMs and channels.
  const dropMessageFromCache = useCallback(
    (key, messageId) => {
      queryClient.setQueryData(key, (previous) => {
        if (!previous || !Array.isArray(previous.pages)) return previous;
        const pages = previous.pages.map((page) => ({
          ...page,
          messages: (page.messages || []).filter((msg) => msg.id !== messageId),
        }));

        return { ...previous, pages };
      });
    },
    [queryClient]
  );

  const handleResend = useCallback(
    (message) => {
      if (channelId) {
        dropMessageFromCache(["chatGroup", tenantVal, channelId], message.id);
        resendChannelMessage.mutate(message.message);
        return;
      }
      if (activeChatStaffId) {
        dropMessageFromCache(["chatDm", tenantVal, activeChatStaffId], message.id);
        resendDirectMessage.mutate(message.message);
      }
    },
    [
      channelId,
      activeChatStaffId,
      tenantVal,
      dropMessageFromCache,
      resendChannelMessage,
      resendDirectMessage,
    ]
  );

  const myStaffId = me?.staffMember?.id;

  const chatPartner = staffMember?.find(
    (staff) => staff.id === activeChatStaffId
  );

  // Both DMs and channels now expose the same paginated infinite-query
  // shape, so a single hook call selected by `channelId` does the job.
  const channelQuery = useGetChannelMessages(channelId);
  const dmQuery = useGetDmMessages(activeChatStaffId);
  const activeQuery = channelId ? channelQuery : dmQuery;
  const {
    data: activeData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = activeQuery;

  // Flatten: pages come page-0-newest, each page is sent_time DESC.
  // Reverse pages and reverse each page so the final array is oldest
  // → newest, ready to render top-to-bottom in the scroll area.
  const chat = useMemo(() => {
    if (!activeData?.pages) return [];
    const flattened = [];
    const reversedPages = [...activeData.pages].reverse();
    for (const page of reversedPages) {
      const messages = page.messages || [];
      flattened.push(...[...messages].reverse());
    }
    flattened.sort((a, b) => {
      const getTime = (msg) => {
        if (Array.isArray(msg.sentTime)) {
          const [year, month, day, hour, minute, second] = msg.sentTime;
          return new Date(year, month - 1, day, hour, minute, second).getTime();
        }
        if (msg.sentTime) return new Date(msg.sentTime).getTime();
        if (msg.createdAt) return new Date(msg.createdAt).getTime();
        return 0;
      };
      return getTime(a) - getTime(b);
    });

    return flattened;
  }, [activeData]);

  const { handleScroll: handleAutoScroll } = useScrollChatToNewestMessage(
    messagesEndRef,
    currentView,
    scrollAreaRef,
    chat
  );

  // Fire mark-read whenever this conversation gains focus (open) or
  // receives a new message while focused. The mutation is idempotent
  // and the marker only moves forward, so duplicate fires are safe.
  // We key on the latest message id rather than length so an edited
  // tail doesn't retrigger.
  const latestMessageId = chat?.length ? chat[chat.length - 1]?.id : null;
  useEffect(() => {
    if (channelId) {
      markGroupRead.mutate({ groupId: channelId });
    } else if (activeChatStaffId) {
      markDmRead.mutate({ partnerStaffId: activeChatStaffId });
    }
    // markDmRead / markGroupRead identities are stable across renders;
    // omitting them keeps the effect from firing on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, activeChatStaffId, latestMessageId]);

  // Load older messages when the user scrolls near the top of the
  // pane. DMs and channels both paginate now, so a single check covers
  // both surfaces.
  const handleScroll = useCallback(
    (e) => {
      handleAutoScroll(e);

      if (!hasNextPage || isFetchingNextPage) return;

      const el = scrollAreaRef.current;
      if (!el) return;

      if (el.scrollTop < 60) {
        const previousScrollHeight = el.scrollHeight;
        fetchNextPage().then(() => {
          requestAnimationFrame(() => {
            if (el) {
              const newScrollHeight = el.scrollHeight;
              el.scrollTop = newScrollHeight - previousScrollHeight;
            }
          });
        });
      }
    },
    [handleAutoScroll, hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const handleEditMessage = async (originalMessage) => {
    const nextText = editText.trim();
    if (!nextText || !originalMessage) return;
    if (nextText === originalMessage.message) {
      setEditingMessage(null);
      setEditText("");
      setEditError(null);
      return;
    }

    try {
      await editMessage.mutateAsync({
        // Full original object on the wire — the server overwrites
        // every column from the body, so a partial { message } would
        // null out chatGroupId / assignToStaffId / parentChatId.
        originalMessage,
        message: nextText,
      });
      setEditingMessage(null);
      setEditText("");
      setEditError(null);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setEditError("You can only edit your own messages.");
        return;
      }
      if (status === 404) {
        // Message is gone server-side — close the editor; the hook
        // already triggered a thread refresh.
        setEditingMessage(null);
        setEditText("");
        setEditError(null);
      }
    }
  };

  const startEditing = (message) => {
    setEditingMessage(message.id);
    setEditText(message.message);
    setEditError(null);
  };

  // Author check — the only place we let the edit affordance light up.
  // Backend doesn't enforce ownership yet, so this is the real guard.
  // We also block edits on optimistic temp messages (no real id) and
  // on messages still in-flight or marked failed, since the server
  // doesn't know about them yet.
  const canEditMessage = (message) => {
    if (!message?.id || String(message.id).startsWith("temp-")) return false;
    if (message?._status === "sending" || message?._status === "failed") {
      return false;
    }
    if (message?.deleted) return false;
    const authorId =
      message?.createdByStaffId ??
      message?.createdByStaff?.id ??
      message?.staffMember?.id ??
      null;

    return (
      myStaffId != null &&
      authorId != null &&
      Number(authorId) === Number(myStaffId)
    );
  };

  // Check if we can create a task from a message (any real message)
  const canCreateTask = (message) => {
    if (!message?.id || String(message.id).startsWith("temp-")) return false;
    if (message?._status === "sending" || message?._status === "failed") {
      return false;
    }

    return true;
  };

  // Get the staff ID to assign the task to (the other person in the chat)
  const getTaskAssigneeId = (message) => {
    // For DMs: assign to the chat partner
    if (activeChatStaffId) {
      return String(activeChatStaffId);
    }

    // For channels: assign to the message sender (if not yourself)
    const senderId =
      message?.createdByStaffId ??
      message?.createdByStaff?.id ??
      message?.staffMember?.id;

    if (senderId && Number(senderId) !== Number(myStaffId)) {
      return String(senderId);
    }

    // Fallback: don't pre-assign
    return "";
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "";

    let date;
    // Handle sentTime array format: [year, month, day, hour, minute, second, nano]
    if (Array.isArray(dateValue)) {
      const [year, month, day, hour, minute, second] = dateValue;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else {
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) return "";

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    if (isToday) {
      return `Today at ${timeStr}`;
    }

    const dateStr = date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    return `${dateStr} at ${timeStr}`;
  };

  // Get sender name from message (for channel messages)
  const getSenderName = (message, isMessageYours) => {
    if (isMessageYours) return "You";

    // Check for sender info in the message itself (channel messages)
    const staff = message.createdByStaff || message.staffMember;
    if (staff) {
      const { firstName, lastName } = staff;
      return `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
    }

    if (message.senderFirstName || message.senderLastName) {
      return `${message.senderFirstName || ""} ${message.senderLastName || ""}`.trim();
    }

    if (message.senderName) {
      return message.senderName;
    }

    // Fallback to chatPartner for direct messages
    if (chatPartner) {
      return `${chatPartner.firstName || ""} ${chatPartner.lastName || ""}`.trim();
    }

    return "Unknown";
  };

  return (
    <ScrollArea
      style={{
        height: "100%",
      }}
      onScroll={handleScroll}
      ref={scrollAreaRef}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          padding: "0.75rem",
        }}
      >
        {/* Loading indicator when fetching older messages */}
        {isFetchingNextPage && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "8px",
              color: "#9ca3af",
              fontSize: "0.8125rem",
            }}
          >
            Loading older messages...
          </div>
        )}

        {chat?.map((message) => {
          // Three ways a message can be "yours":
          // 1. Backend DM payload encodes ownership via assignToStaff.tenant
          // 2. The send hooks tag optimistic messages with _outgoing
          // 3. Channel messages come back with createdByStaffId === tenant
          const isMessageYours =
            message?.assignToStaff?.tenant === Number(tenantVal) ||
            message?._outgoing === true ||
            Number(message?.createdByStaffId) === Number(tenantVal);
          const sendStatus = message?._status;

          return (
            <div
              key={message.id}
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                padding: "8px 10px",
                borderRadius: "6px",
                transition: "background-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f9fafb";
                setActiveMessageMenu(message.id);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                setActiveMessageMenu(null);
              }}
            >
              {/* Message Actions Menu — edit for your own messages, create task for any */}
              {activeMessageMenu === message.id && canCreateTask(message) && (
                <div
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "8px",
                    display: "flex",
                    gap: "2px",
                    padding: "4px",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    zIndex: 10,
                  }}
                >
                  {onCreateTask && (
                    <button
                      onClick={() =>
                        onCreateTask({
                          message,
                          assigneeId: getTaskAssigneeId(message),
                          senderName: getSenderName(
                            message,
                            Number(message?.createdByStaffId) === Number(myStaffId)
                          ),
                          timestamp: formatDateTime(message.sentTime || message.createdAt),
                        })
                      }
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        padding: "4px 6px",
                        borderRadius: "4px",
                      }}
                      title="Create task from message"
                    >
                      <ListTodo style={{ height: "14px", width: "14px", color: "#6b7280" }} />
                    </button>
                  )}
                  {canEditMessage(message) && (
                    <button
                      onClick={() => startEditing(message)}
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        padding: "4px 6px",
                        borderRadius: "4px",
                      }}
                      title="Edit message"
                    >
                      <Edit2 style={{ height: "14px", width: "14px", color: "#6b7280" }} />
                    </button>
                  )}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "0.6rem",
                }}
              >
                {(() => {
                  // Derive the sender's identity from whichever shape
                  // the backend supplied (channel messages embed full
                  // staff, DMs sometimes only carry id + flat first /
                  // last names, and direct chats fall back to the
                  // chat partner). We hash on the resulting staff id
                  // so the colour matches the sidebar exactly.
                  const senderStaff =
                    message.createdByStaff || message.staffMember;
                  const idForColor =
                    senderStaff?.id ??
                    message.createdByStaffId ??
                    (isMessageYours
                      ? myStaffId
                      : chatPartner?.id ?? null);
                  const profilePic =
                    senderStaff?.profilePic ||
                    message.avatar ||
                    (isMessageYours
                      ? me?.staffMember?.profilePic || null
                      : chatPartner?.profilePic || null);
                  const fallbackInitials = isMessageYours
                    ? initialsFromName(
                        me?.staffMember?.firstName,
                        me?.staffMember?.lastName
                      )
                    : initialsFromName(
                        senderStaff?.firstName || message.senderFirstName,
                        senderStaff?.lastName || message.senderLastName
                      ) ||
                      initialsFromName(
                        chatPartner?.firstName,
                        chatPartner?.lastName
                      );
                  return (
                    <MessageAvatar
                      profilePic={profilePic}
                      idForColor={idForColor}
                      initials={fallbackInitials}
                    />
                  );
                })()}

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Sender name and date/time */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                    <span style={{ fontWeight: 600, fontSize: "0.875rem", color: "#111827" }}>
                      {getSenderName(message, isMessageYours)}
                    </span>
                    <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                      {formatDateTime(message.sentTime || message.createdAt)}
                    </span>
                    {(message.isEdited || message.edited) && (
                      <span style={{ fontSize: "0.6875rem", color: "#9ca3af", fontStyle: "italic" }}>
                        (edited)
                      </span>
                    )}
                  </div>

                  {/* Message content or edit input */}
                  {editingMessage === message.id ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          style={{
                            flex: 1,
                            padding: "6px 10px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            fontSize: "0.875rem",
                            outline: "none",
                          }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleEditMessage(message);
                            } else if (e.key === "Escape") {
                              setEditingMessage(null);
                              setEditText("");
                              setEditError(null);
                            }
                          }}
                        />
                        <button
                          onClick={() => handleEditMessage(message)}
                          disabled={editMessage.isPending}
                          style={{
                            border: "none",
                            backgroundColor: "#22c55e",
                            color: "#fff",
                            padding: "6px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Check style={{ height: "14px", width: "14px" }} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingMessage(null);
                            setEditText("");
                            setEditError(null);
                          }}
                          style={{
                            border: "none",
                            backgroundColor: "#ef4444",
                            color: "#fff",
                            padding: "6px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <X style={{ height: "14px", width: "14px" }} />
                        </button>
                      </div>
                      {editError && (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#b45309",
                          }}
                        >
                          {editError}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: "#374151",
                          lineHeight: 1.5,
                          flex: "0 1 auto",
                          minWidth: 0,
                          wordBreak: "break-word",
                        }}
                      >
                        {message.message}
                      </span>
                      {isMessageYours && sendStatus === "failed" && (
                        <button
                          type="button"
                          onClick={() => handleResend(message)}
                          title="Failed to send — click to retry"
                          aria-label="Failed to send — click to retry"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            border: "none",
                            backgroundColor: "transparent",
                            padding: 0,
                            cursor: "pointer",
                            color: "#dc2626",
                            flexShrink: 0,
                          }}
                        >
                          <AlertCircle style={{ height: "14px", width: "14px" }} />
                        </button>
                      )}
                      {isMessageYours && sendStatus === "sending" && (
                        <Loader2
                          aria-label="Sending"
                          className={chatWidgetStyles.sendStatusSpin}
                          style={{
                            height: "12px",
                            width: "12px",
                            color: "#9ca3af",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {isMessageYours && !sendStatus && (() => {
                        // Branch on conversation type — group messages
                        // expose readBy[]; DM messages expose
                        // readByRecipient + readAt. The single grey
                        // tick means "delivered, not yet read"; the
                        // green double-tick means "the other side has
                        // moved their read marker past this message".
                        if (channelId) {
                          const readerCount = Array.isArray(message.readBy)
                            ? message.readBy.length
                            : 0;
                          if (readerCount > 0) {
                            return (
                              <span
                                title={`Seen by ${readerCount}`}
                                aria-label={`Seen by ${readerCount}`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "3px",
                                  fontSize: "0.6875rem",
                                  color: "#22c55e",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                <CheckCheck
                                  style={{ height: "12px", width: "12px" }}
                                />
                                {readerCount}
                              </span>
                            );
                          }

                          return (
                            <Check
                              aria-label="Sent"
                              style={{
                                height: "12px",
                                width: "12px",
                                color: "#9ca3af",
                                flexShrink: 0,
                              }}
                            />
                          );
                        }

                        if (message.readByRecipient) {
                          const seenLabel = message.readAt
                            ? `Seen ${formatDateTime(message.readAt)}`
                            : "Seen";

                          return (
                            <CheckCheck
                              aria-label={seenLabel}
                              title={seenLabel}
                              style={{
                                height: "12px",
                                width: "12px",
                                color: "#22c55e",
                                flexShrink: 0,
                              }}
                            />
                          );
                        }

                        return (
                          <Check
                            aria-label="Sent"
                            title="Sent"
                            style={{
                              height: "12px",
                              width: "12px",
                              color: "#9ca3af",
                              flexShrink: 0,
                            }}
                          />
                        );
                      })()}
                    </div>
                  )}

                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
};
